import { useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, Loader2, Inbox, Mail, Phone, MapPin, Calendar, PoundSterling, Tag } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("tyrell_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": k } });
      setEnquiries(res.data);
      setAuthed(true);
      sessionStorage.setItem("tyrell_admin_key", k);
    } catch {
      setError("Invalid admin key.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen bg-[#050B14] px-5 py-10 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between">
          <Logo />
          <a href="/" data-testid="admin-back-link" className="text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-[#C5A059]">
            Back to Site
          </a>
        </div>

        {!authed ? (
          <form
            data-testid="admin-login-form"
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
            className="mx-auto mt-24 max-w-md border border-[#C5A059]/25 bg-[#0A111E] p-10"
          >
            <KeyRound size={28} className="text-[#C5A059]" />
            <h1 className="mt-5 font-serif text-2xl uppercase tracking-wide text-white">Enquiries Dashboard</h1>
            <p className="mt-2 text-sm font-light text-slate-400">Enter the admin key to view submitted enquiries.</p>
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin key"
              className="mt-6 w-full border border-[#C5A059]/20 bg-[#0E1726] px-4 py-3.5 text-sm text-white outline-none focus:border-[#C5A059]"
            />
            {error && <p data-testid="admin-error" className="mt-3 text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="mt-6 flex w-full items-center justify-center gap-2 bg-[#C5A059] py-3.5 text-xs font-bold uppercase tracking-[0.3em] text-[#050B14] transition-colors hover:bg-[#E6CA65] disabled:opacity-60"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              View Enquiries
            </button>
          </form>
        ) : (
          <div className="mt-12">
            <div className="flex items-end justify-between">
              <h1 className="font-serif text-3xl uppercase tracking-wide text-white">
                Enquiries <span className="gold-text-gradient">({enquiries.length})</span>
              </h1>
              <button
                data-testid="admin-refresh-button"
                onClick={() => load(key)}
                className="border border-[#C5A059]/40 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] transition-colors hover:bg-[#C5A059] hover:text-[#050B14]"
              >
                Refresh
              </button>
            </div>

            {enquiries.length === 0 ? (
              <div data-testid="admin-empty-state" className="mt-10 flex flex-col items-center border border-dashed border-[#C5A059]/30 py-20 text-center">
                <Inbox size={32} className="text-[#C5A059]/50" />
                <p className="mt-4 text-sm text-slate-400">No enquiries yet. New submissions will appear here.</p>
              </div>
            ) : (
              <div className="mt-8 space-y-4">
                {enquiries.map((q) => (
                  <article key={q.id} data-testid={`enquiry-card-${q.id}`} className="border border-[#C5A059]/20 bg-[#0A111E] p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h2 className="font-serif text-xl text-white">{q.name}</h2>
                      <span className="border border-[#C5A059]/40 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#C5A059]">
                        {q.status}
                      </span>
                    </div>
                    <div className="mt-4 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
                      <span className="flex items-center gap-2"><Mail size={13} className="text-[#C5A059]" />{q.email}</span>
                      {q.phone && <span className="flex items-center gap-2"><Phone size={13} className="text-[#C5A059]" />{q.phone}</span>}
                      {q.property_address && <span className="flex items-center gap-2"><MapPin size={13} className="text-[#C5A059]" />{q.property_address}</span>}
                      {q.project_type && <span className="flex items-center gap-2"><Tag size={13} className="text-[#C5A059]" />{q.project_type}</span>}
                      {q.budget && <span className="flex items-center gap-2"><PoundSterling size={13} className="text-[#C5A059]" />{q.budget}</span>}
                      {q.start_date && <span className="flex items-center gap-2"><Calendar size={13} className="text-[#C5A059]" />{q.start_date}</span>}
                    </div>
                    {q.description && <p className="mt-4 border-t border-[#C5A059]/10 pt-4 text-sm font-light leading-relaxed text-slate-400">{q.description}</p>}
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                      {new Date(q.created_at).toLocaleString("en-GB")}
                    </p>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
